import type {
  AbilityId,
  BoardConfig,
  Pos,
  ResolveResult,
  RunStats,
  Stage,
} from "./types";
import { Engine, type EngineModifiers } from "./engine";
import { MAX_TIER, TIER_POINTS } from "./tiers";
import { emptyStats, isCleared, evalStars } from "./objectives";
import { DAILY_DROP_LIMIT, dailyConfig } from "./daily";

export type Outcome = "playing" | "won" | "lost";

export interface SessionMode {
  kind: "endless" | "campaign" | "daily";
  stage?: Stage;
  /** Column-count override: endless play-time picker, or campaign auto-convert. */
  cols?: number;
}

/** Selectable board widths (spec: 4/5/6-column modes). */
export const COLS_OPTIONS = [4, 5, 6] as const;
export const DEFAULT_COLS = 4;

function clampCols(c: number | undefined, fallback: number): number {
  if (c == null || !Number.isFinite(c)) return fallback;
  return Math.min(6, Math.max(4, Math.floor(c)));
}

// Richer spawn so the 3-merge tier climb stays reachable (spec balancing).
const ENDLESS_SPAWN = { "1": 0.32, "2": 0.28, "3": 0.22, "4": 0.13, "5": 0.05 };

function endlessConfig(cols: number): BoardConfig {
  return { cols, rows: 8, spawnTable: ENDLESS_SPAWN, maxTier: MAX_TIER };
}

/** Rescale blocked-cell columns when a stage is auto-converted to a new width. */
function remapBlocked(
  cells: ReadonlyArray<[number, number]> | undefined,
  oldCols: number,
  newCols: number,
): ReadonlyArray<[number, number]> | undefined {
  if (!cells || cells.length === 0 || oldCols <= 1) return cells;
  const seen = new Set<string>();
  const out: [number, number][] = [];
  for (const [r, c] of cells) {
    const scaled = Math.round((c * (newCols - 1)) / (oldCols - 1));
    const cc = Math.min(newCols - 1, Math.max(0, scaled));
    const key = `${r},${cc}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([r, cc]);
  }
  return out;
}

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : 0;
}

/**
 * Wraps an Engine with run-stat tracking, stage constraints, objective/star
 * evaluation, and win/lose resolution. The UI talks to this, not the Engine.
 */
export class GameSession {
  readonly engine: Engine;
  readonly mode: SessionMode;
  readonly stage: Stage | null;
  stats: RunStats = emptyStats();
  outcome: Outcome = "playing";
  private startMs = nowMs();

  constructor(
    mode: SessionMode,
    seed: string,
    modifiers?: Partial<EngineModifiers>,
    previewCount?: number,
  ) {
    this.mode = mode;
    this.stage = mode.stage ?? null;
    const config = this.stage
      ? this.configFromStage(this.stage, mode.cols)
      : mode.kind === "daily"
        ? dailyConfig(clampCols(mode.cols, DEFAULT_COLS))
        : endlessConfig(clampCols(mode.cols, DEFAULT_COLS));
    this.engine = new Engine({ config, seed, tierPoints: TIER_POINTS, modifiers, previewCount });
  }

  get isDaily(): boolean {
    return this.mode.kind === "daily";
  }

  private configFromStage(stage: Stage, colsOverride?: number): BoardConfig {
    // Stages with preset boards (puzzles) are designed for an exact width —
    // never remap them to the player's chosen column count.
    const fixedLayout = (stage.constraints.presetCells?.length ?? 0) > 0;
    const cols = fixedLayout ? stage.gridCols : clampCols(colsOverride, stage.gridCols);
    const blockedCells =
      cols === stage.gridCols
        ? stage.constraints.blockedCells
        : remapBlocked(stage.constraints.blockedCells, stage.gridCols, cols);
    // Bosses rescale their column the same way blocked cells do.
    const bosses =
      cols === stage.gridCols
        ? stage.constraints.bosses
        : stage.constraints.bosses?.map(([r, c, hp]): [number, number, number] => {
            const scaled = Math.round((c * (cols - 1)) / (stage.gridCols - 1));
            return [r, Math.min(cols - 1, Math.max(0, scaled)), hp];
          });
    return {
      cols,
      rows: stage.gridRows,
      spawnTable: stage.constraints.spawnTable,
      blockedCells,
      bosses,
      presetCells: stage.constraints.presetCells,
      maxTier: MAX_TIER,
      mergeMin: stage.constraints.mergeMin,
    };
  }

  /** Remaining drops, or null when unlimited (endless / no drop limit). */
  get dropsRemaining(): number | null {
    const limit = this.isDaily ? DAILY_DROP_LIMIT : this.stage?.constraints.dropLimit;
    if (limit == null) return null;
    return Math.max(0, limit - this.engine.drops);
  }

  /** Seconds remaining for a time-limited stage, or null otherwise. */
  get secondsRemaining(): number | null {
    const limit = this.stage?.constraints.timeLimit;
    if (limit == null) return null;
    const elapsed = (nowMs() - this.startMs) / 1000;
    return Math.max(0, Math.ceil(limit - elapsed));
  }

  /**
   * Tick the clock and re-evaluate. The play UI calls this periodically so
   * time-limited stages end on schedule even when the player stops dropping.
   */
  tick(): void {
    if (this.outcome !== "playing") return;
    this.stats.elapsedMs = nowMs() - this.startMs;
    this.evaluate();
  }

  /** Abilities allowed this stage, or null for "all" (endless). */
  get allowedAbilities(): AbilityId[] | null {
    const allowed = this.stage?.constraints.allowedAbilities;
    return allowed ? (allowed as AbilityId[]) : null;
  }

  isAbilityAllowed(id: AbilityId): boolean {
    const allowed = this.allowedAbilities;
    return !allowed || allowed.includes(id);
  }

  get finished(): boolean {
    return this.outcome !== "playing";
  }

  /** Stars earned right now (for live/result display). */
  stars(): number {
    if (!this.stage) return 0;
    return evalStars(this.stage, this.stats, this.outcome === "won");
  }

  private ingest(result: ResolveResult): void {
    this.stats.score = this.engine.score;
    this.stats.drops = this.engine.drops;
    this.stats.maxChain = this.engine.maxChain;
    this.stats.maxTier = this.engine.maxTierReached;
    this.stats.merges += result.merges.length;
    this.stats.bossesBroken += result.bossesBroken;
    for (const m of result.merges) {
      this.stats.tierCreated[m.toTier] = (this.stats.tierCreated[m.toTier] ?? 0) + 1;
    }
  }

  private evaluate(): void {
    if (this.outcome !== "playing") return;
    this.stats.elapsedMs = nowMs() - this.startMs;

    if (this.isDaily) {
      // Daily is a score run — finishing (out of drops or board full) is a "win".
      if (this.engine.gameOver || this.dropsRemaining === 0) this.outcome = "won";
      return;
    }
    if (this.stage) {
      if (isCleared(this.stage, this.stats)) {
        this.outcome = "won";
        return;
      }
      if (
        this.engine.gameOver ||
        this.dropsRemaining === 0 ||
        this.secondsRemaining === 0
      ) {
        this.outcome = "lost";
        return;
      }
    } else if (this.engine.gameOver) {
      this.outcome = "lost";
    }
  }

  /** Whether a drop is currently permitted. */
  canDrop(): boolean {
    return (
      !this.finished &&
      !this.engine.gameOver &&
      (this.dropsRemaining ?? 1) > 0 &&
      (this.secondsRemaining ?? 1) > 0
    );
  }

  drop(col: number): ResolveResult | null {
    if (!this.canDrop()) return null;
    // Snapshot which piece is about to be dropped so we can flag special-use.
    const piece = this.engine.current;
    const result = this.engine.drop(col);
    if (!result) return null;
    if (piece === -1) this.usedWild = true;
    if (piece === -2) this.usedBomb = true;
    this.ingest(result);
    this.evaluate();
    return result;
  }

  /** Distinct ability ids used this run (for achievements). */
  readonly abilityIdsUsed: Set<string> = new Set();
  /** Whether a wild was dropped this run (for the use_wild achievement). */
  usedWild = false;
  /** Whether a bomb was dropped this run (for the use_bomb achievement). */
  usedBomb = false;

  useAbility(id: AbilityId, picks: Pos[]): ResolveResult | "reroll" | null {
    if (this.finished || !this.isAbilityAllowed(id)) return null;
    let result: ResolveResult | null = null;
    if (id === "reroll") {
      if (!this.engine.useReroll()) return null;
      this.stats.abilitiesUsed += 1;
      this.abilityIdsUsed.add(id);
      return "reroll";
    }
    if (id === "hammer") result = this.engine.useHammer(picks[0]);
    else if (id === "promote") result = this.engine.usePromote(picks[0]);
    else if (id === "swap") result = this.engine.useSwap(picks[0], picks[1]);
    if (!result) return null;
    this.stats.abilitiesUsed += 1;
    this.abilityIdsUsed.add(id);
    this.ingest(result);
    this.evaluate();
    return result;
  }
}
